import axios from "./AxiosClient";
import { Constants } from "@socialcoach/services";
import { scCatchServiceException } from "@/annotations";
import {
  DesignPackageActivityEvent,
  ActivityListParams,
  DesignPackageDetail,
  DesignPackageListParams,
  DesignPackageStatsResponse,
  DesignPackageSummary,
  DesignPackageTrace,
  DesignPackageTraceParams,
  DesignRenderGroup,
  DuplicateDesignPackageRequest,
  GenerateDesignPackageRequest,
  Page,
  PreviewSet,
  PreviewsParams,
  PublishDesignPackageRequest,
  RefineDesignPackageRequest,
  RenderPreviewRequest,
  RevisionRequest,
  UnpublishDesignPackageRequest,
  UnpublishDesignPackageResponse,
  UpdateDesignPackageRequest,
  UpdateDynamicPromptRequest,
} from "./dtos";

const DESIGN_PACKAGE_SERVICE = `${Constants.API_URL}/bedrock/design_packages`;

/**
 * Every method rethrows the parsed `{ error, message, details }` envelope so callers can
 * discriminate with `isApiError(e, code)`.
 */
export class DesignPackageServices {
  @scCatchServiceException(true)
  static async list(params: DesignPackageListParams): Promise<Page<DesignPackageSummary>> {
    const { data } = await axios.get<Page<DesignPackageSummary>>(DESIGN_PACKAGE_SERVICE, { params });
    return data;
  }

  @scCatchServiceException(true)
  static async get(id: number): Promise<DesignPackageDetail> {
    const { data } = await axios.get<DesignPackageDetail>(`${DESIGN_PACKAGE_SERVICE}/${id}`);
    return data;
  }

  @scCatchServiceException(true)
  static async generate(request: GenerateDesignPackageRequest): Promise<DesignPackageDetail> {
    const { data } = await axios.post<DesignPackageDetail>(`${DESIGN_PACKAGE_SERVICE}/generate`, request);
    return data;
  }

  @scCatchServiceException(true)
  static async retry(id: number): Promise<DesignPackageDetail> {
    const { data } = await axios.post<DesignPackageDetail>(`${DESIGN_PACKAGE_SERVICE}/${id}/retry`, {});
    return data;
  }

  @scCatchServiceException(true)
  static async update(id: number, request: UpdateDesignPackageRequest): Promise<DesignPackageDetail> {
    const { data } = await axios.patch<DesignPackageDetail>(`${DESIGN_PACKAGE_SERVICE}/${id}`, request);
    return data;
  }

  @scCatchServiceException(true)
  static async archive(id: number): Promise<void> {
    await axios.delete(`${DESIGN_PACKAGE_SERVICE}/${id}`);
  }

  @scCatchServiceException(true)
  static async restore(id: number): Promise<DesignPackageDetail> {
    const { data } = await axios.post<DesignPackageDetail>(`${DESIGN_PACKAGE_SERVICE}/${id}/restore`);
    return data;
  }

  @scCatchServiceException(true)
  static async duplicate(id: number, request: DuplicateDesignPackageRequest = {}): Promise<DesignPackageDetail> {
    const { data } = await axios.post<DesignPackageDetail>(`${DESIGN_PACKAGE_SERVICE}/${id}/duplicate`, request);
    return data;
  }

  @scCatchServiceException(true)
  static async refine(id: number, request: RefineDesignPackageRequest): Promise<DesignPackageDetail> {
    const { data } = await axios.post<DesignPackageDetail>(`${DESIGN_PACKAGE_SERVICE}/${id}/refine`, request);
    return data;
  }

  @scCatchServiceException(true)
  static async approveRevision(id: number, request: RevisionRequest = {}): Promise<DesignPackageDetail> {
    const { data } = await axios.post<DesignPackageDetail>(`${DESIGN_PACKAGE_SERVICE}/${id}/approve_revision`, request);
    return data;
  }

  @scCatchServiceException(true)
  static async discardRevision(id: number, request: RevisionRequest = {}): Promise<DesignPackageDetail> {
    const { data } = await axios.post<DesignPackageDetail>(`${DESIGN_PACKAGE_SERVICE}/${id}/discard_revision`, request);
    return data;
  }

  @scCatchServiceException(true)
  static async publish(id: number, request: PublishDesignPackageRequest = {}): Promise<DesignPackageDetail> {
    const { data } = await axios.post<DesignPackageDetail>(`${DESIGN_PACKAGE_SERVICE}/${id}/publish`, request);
    return data;
  }

  @scCatchServiceException(true)
  static async unpublish(id: number, request?: UnpublishDesignPackageRequest): Promise<UnpublishDesignPackageResponse> {
    const { data } = await axios.post<UnpublishDesignPackageResponse>(`${DESIGN_PACKAGE_SERVICE}/${id}/unpublish`, {
      deleteScheduledPosts: request?.deleteScheduledPosts ?? false,
    });
    return data;
  }

  @scCatchServiceException(true)
  static async updatePrompt(id: number, request: UpdateDynamicPromptRequest): Promise<DesignPackageDetail> {
    const { data } = await axios.patch<DesignPackageDetail>(`${DESIGN_PACKAGE_SERVICE}/${id}/prompt`, request);
    return data;
  }

  @scCatchServiceException(true)
  static async previews(id: number, params: PreviewsParams): Promise<PreviewSet[]> {
    const search = new URLSearchParams();
    params.themeIds.forEach(themeId => search.append("themeIds[]", themeId === null ? "null" : String(themeId)));
    if (params.includeCandidates !== undefined) search.set("includeCandidates", String(params.includeCandidates));
    const { data } = await axios.get<PreviewSet[]>(`${DESIGN_PACKAGE_SERVICE}/${id}/previews?${search.toString()}`);
    return data;
  }

  @scCatchServiceException(true)
  static async renderPreview(id: number, request: RenderPreviewRequest): Promise<DesignRenderGroup> {
    const { data } = await axios.post<DesignRenderGroup>(`${DESIGN_PACKAGE_SERVICE}/${id}/previews`, request);
    return data;
  }

  @scCatchServiceException(true)
  static async activity(id: number, params: ActivityListParams = {}): Promise<Page<DesignPackageActivityEvent>> {
    const { data } = await axios.get<Page<DesignPackageActivityEvent>>(`${DESIGN_PACKAGE_SERVICE}/${id}/activity`, {
      params,
    });
    return data;
  }

  @scCatchServiceException(true)
  static async stats(id: number): Promise<DesignPackageStatsResponse> {
    const { data } = await axios.get<DesignPackageStatsResponse>(`${DESIGN_PACKAGE_SERVICE}/${id}/stats`);
    return data;
  }

  /** Admin only; 403 otherwise. */
  @scCatchServiceException(true)
  static async trace(id: number, params: DesignPackageTraceParams = {}): Promise<DesignPackageTrace> {
    const { data } = await axios.get<DesignPackageTrace>(`${DESIGN_PACKAGE_SERVICE}/${id}/trace`, { params });
    return data;
  }
}
